// ── Map Callout Labels ──
// Callout position data for minimap labels.
// Each callout has a name, 2D position (world x/z), and a zone classification.

export type CalloutZone =
  | 'a_site'
  | 'b_site'
  | 'mid'
  | 'ct_spawn'
  | 't_spawn'
  | 'connector'
  | 'other';

export interface MapCallout {
  name: string;
  position: { x: number; z: number };
  zone: CalloutZone;
}

// ── de_outpost_v6 callouts ──
// Phase Integration. Positions reflect the modular FOB rebuild: 10 canonical
// zones (PLAN.md §8) + multi-storey catwalks/ladders introduced by v6 hero
// buildings. Coordinates are server world-space (arenaHalf = 80, +Z = T side).
const DE_OUTPOST_V6_CALLOUTS: MapCallout[] = [
  // A Site / Command Center
  { name: 'A Site',         position: { x: -48, z: -38 }, zone: 'a_site' },
  { name: 'A Long Door',    position: { x: -48, z: -29 }, zone: 'a_site' },
  { name: 'A Short Door',   position: { x: -36, z: -38 }, zone: 'a_site' },
  { name: 'A Balcony',      position: { x: -48, z: -30 }, zone: 'a_site' },
  { name: 'A Roof',         position: { x: -44, z: -46 }, zone: 'a_site' },

  // B Site / Warehouse
  { name: 'B Site',         position: { x:  48, z: -13 }, zone: 'b_site' },
  { name: 'Warehouse Cell', position: { x:  38, z: -19 }, zone: 'b_site' },
  { name: 'B Catwalk',      position: { x:  60, z: -25 }, zone: 'b_site' },
  { name: 'Loading Dock',   position: { x:  48, z:  -7 }, zone: 'b_site' },

  // Mid
  { name: 'Mid Courtyard',  position: { x:   0, z:  20 }, zone: 'mid' },
  { name: 'Mid Tower',      position: { x:   0, z:   8 }, zone: 'mid' },

  // Connectors / lanes
  { name: 'Long A',         position: { x: -50, z:  15 }, zone: 'connector' },
  { name: 'Short A',        position: { x: -15, z:  10 }, zone: 'connector' },
  { name: 'B Tunnels',      position: { x:  50, z:  20 }, zone: 'connector' },
  { name: 'B Upper CW',     position: { x:  53, z:  12 }, zone: 'connector' },
  { name: 'CT-A Tunnel',    position: { x: -20, z: -15 }, zone: 'connector' },
  { name: 'Drainage',       position: { x:  20, z: -25 }, zone: 'connector' },

  // CT Spawn / HQ Compound
  { name: 'CT Spawn',       position: { x:   0, z: -60 }, zone: 'ct_spawn' },
  { name: 'CT Watchtower',  position: { x:  22, z: -76 }, zone: 'ct_spawn' },
  { name: 'CT Armory',      position: { x: -11, z: -66 }, zone: 'ct_spawn' },

  // T Spawn / Staging Yard
  { name: 'T Spawn',        position: { x:   0, z:  70 }, zone: 't_spawn' },
  { name: 'T Barracks',     position: { x: -10, z:  74 }, zone: 't_spawn' },
  { name: 'T Comms',        position: { x:  12, z:  81 }, zone: 't_spawn' },
];

// ── dust2 callouts ──
// Classic 3-lane desert bazaar (scripts/build-dust2.mjs, arenaHalf = 80).
// T spawn sits at +Z (north of the plaza), CT spawn at -Z (south), A site is
// the west wing past Long, B site is the east wing past Tunnels.
const DUST2_CALLOUTS: MapCallout[] = [
  // T side (north)
  { name: 'T Spawn',        position: { x:   0, z:  68 }, zone: 't_spawn' },

  // Long A lane (west, T → A)
  { name: 'Long Doors',     position: { x: -32, z:  14 }, zone: 'connector' },
  { name: 'Long A',         position: { x: -38, z:  20 }, zone: 'connector' },
  { name: 'Pit',            position: { x: -46, z: -30 }, zone: 'a_site' },

  // A site / Short A (west)
  { name: 'A Site',         position: { x: -50, z: -40 }, zone: 'a_site' },
  { name: 'A Short',        position: { x: -20, z: -10 }, zone: 'connector' },
  { name: 'Cat',            position: { x: -12, z:  -8 }, zone: 'connector' },

  // Mid lane
  { name: 'Mid',            position: { x:   0, z:  10 }, zone: 'mid' },
  { name: 'Mid Doors',      position: { x:  -5, z:  15 }, zone: 'mid' },
  { name: 'Xbox',           position: { x:  -2, z:  -2 }, zone: 'mid' },

  // B Tunnels / B site (east, T → B)
  { name: 'B Tunnels',      position: { x:  22, z:  18 }, zone: 'connector' },
  { name: 'B Default',      position: { x:  44, z: -26 }, zone: 'b_site' },
  { name: 'B Site',         position: { x:  45, z: -30 }, zone: 'b_site' },

  // CT side (south)
  { name: 'Goose',          position: { x:  10, z: -50 }, zone: 'connector' },
  { name: 'CT Spawn',       position: { x:   0, z: -68 }, zone: 'ct_spawn' },
];

// ── de_neon callouts ──
// Cyberpunk underground (scripts/build-neon.mjs, arenaHalf = 60). Note: the
// actual map runs the T → CT lane east → west (+X → -X), with A site as an
// elevated rooftop bar (Y=+6) and B site as an underground parking deck
// (Y=-4). Mid is the nightclub interior near -X mid.
const DE_NEON_CALLOUTS: MapCallout[] = [
  // T side (east)
  { name: 'T Spawn',        position: { x:  52, z:   0 }, zone: 't_spawn' },
  { name: 'Neon Alley',     position: { x:  30, z:   4 }, zone: 'connector' },

  // A site (elevated rooftop bar, east-of-mid, Y≈+6)
  { name: 'A Site',         position: { x:  15, z: -20 }, zone: 'a_site' },
  { name: 'Server Room',    position: { x:  20, z: -10 }, zone: 'a_site' },
  { name: 'Catwalk',        position: { x:   5, z: -14 }, zone: 'connector' },

  // Mid lane (nightclub interior)
  { name: 'Mid',            position: { x: -20, z:   0 }, zone: 'mid' },
  { name: 'Mid Stairs',     position: { x: -30, z:  12 }, zone: 'mid' },

  // B site (underground parking deck, west-of-mid, Y≈-4)
  { name: 'Underground',    position: { x:  -8, z:  -9 }, zone: 'b_site' },
  { name: 'B Site',         position: { x: -10, z: -25 }, zone: 'b_site' },

  // CT side (west)
  { name: 'CT Spawn',       position: { x: -54, z:   0 }, zone: 'ct_spawn' },
];

const CALLOUT_MAP: Record<string, MapCallout[]> = {
  de_outpost_v6: DE_OUTPOST_V6_CALLOUTS,
  dust2: DUST2_CALLOUTS,
  de_neon: DE_NEON_CALLOUTS,
};

/** Get callout data for a given map name. Returns empty array if unknown. */
export function getCallouts(mapName: string): MapCallout[] {
  return CALLOUT_MAP[mapName] ?? [];
}
